<template>
  <div class='mt-2 mb-0'>
    <b-alert show v-if="locked" variant=danger class='mb-0'>
      <b-row>
        <b-col>
          <strong>{{lockedBy}}</strong> has locked this order. It must be unlocked before edits can be made
        </b-col>
        <b-col cols=* v-if=canLock>
          <b-btn variant=dark @click=toggleLock size=sm v-b-popover.hover="'Only IT can lock/unlock orders!'">
            <i class="fas fa-fw fa-user-lock"></i>
            Unlock Order
          </b-btn>
        </b-col>
      </b-row>
    </b-alert>
    <b-btn-toolbar justify v-else>
      <b-btn-group class='my-1'>
        <b-btn variant=warning :disabled="!canEdit" :to="{ name: 'Edit Order', params: { id: this.order.num }}">
          Edit this Order
        </b-btn>
        <b-btn variant=dark v-if="canLock && !locked" @click=toggleLock v-b-popover.hover="'Only IT can lock/unlock orders!'">
          <i class="fas fa-fw fa-user-lock"></i>
          Lock Order
        </b-btn>
      </b-btn-group>

      <b-btn-group class='my-1'>
        <b-btn variant=dark :disabled="!canDelete" @click=deleteFb :title="hasFBPayments ? cantDeleteMessage : ''">
          Delete FB
        </b-btn>
        <b-btn variant=danger :disabled="!canCancelImport" @click=cancelImport>
          Stop Import
        </b-btn>
        <b-btn v-if=isQuote variant=warning :disabled="!canImportQuote" @click=importQuote>
          Import QUOTE to FB
        </b-btn>
      </b-btn-group>

      <b-btn-group class='my-1'>
        <b-btn variant=danger :disabled="!canImport" @click="toggleCancelModal" :title="hasFBPayments ? cantDeleteMessage : ''">
          <span v-if="inFishbowl">Delete FB / </span>Cancel Web
        </b-btn>
        <b-btn variant=warning :disabled="!canImport" @click="importOrder(false)" :title="hasFBPayments ? cantDeleteMessage : ''">
          Re-Import FB
        </b-btn>
        <b-btn variant=success :disabled="!canImport" @click="importOrder(true)" :title="hasFBPayments ? cantDeleteMessage : ''">
          Re-Import & Issue FB
        </b-btn>
      </b-btn-group>

      <b-btn-group class='my-1'>
        <b-btn variant=primary target=_blank :disabled="!canRMAHC" :to="{ name: 'RMA HC', id: order.num }">
          RMA/HC this Order
        </b-btn>
      </b-btn-group>
    </b-btn-toolbar>

    <b-modal
      ref='status-modal' centered @hidden=reloadPage show
      :no-close-on-backdrop="waitingOnChamp" :no-close-on-esc="waitingOnChamp" :hide-header-close=true
    >
      <div class='d-block text-center' v-html=fishbowlMessage />
      <div class='d-block text-center' v-html=webMessage />
      <div slot='modal-footer' class='w-100'>
        <b-btn variant=primary :disabled=waitingOnChamp @click=toggleStatus>OK</b-btn>
      </div>
    </b-modal>
    <b-modal ref='cancel-confirmation' centered show hide-header>
      <b-alert :show="cancelAlert" variant="danger">{{ cancelAlert }}</b-alert>
      <b-form>
          <b-form-row class="my-1">
            Cancellation Reason (<strong style="color:red;">*</strong>): <b-form-select :options="cancelReasons" v-model="cancelForm.cancelReason" />
          </b-form-row>
          <b-form-row class="my-1">
            <b-form-textarea placeholder="Admin Note (will not be shown to customer)" v-model="cancelForm.adminNote" />
          </b-form-row>
          <b-form-row class="my-1">
            <span>Refund Customer?</span>
            <div class='btn-group btn-group-toggle mx-1 mb-2' style="width:100%">
              <b-btn variant="outline-warning" :pressed="cancelForm.refundType == 'original'" @click="cancelForm.refundType = 'original'">Original Method</b-btn>
              <b-btn variant='outline-secondary' :pressed="cancelForm.refundType == 'storeCredit'" @click="cancelForm.refundType = 'storeCredit'">Store Credit</b-btn>
              <b-btn variant='outline-danger' :pressed="cancelForm.refundType == 'none'" @click="cancelForm.refundType = 'none'">NO REFUND</b-btn>
            </div>
          </b-form-row>
          <b-form-row class="my-1">
            <span class="py-1 mr-2">Notify Customer?</span>
            <b-form-checkbox v-model="cancelForm.shouldNotify" />
          </b-form-row>
          <b-form-row class="mt-2">
            <center>
              <h3>DANGER!!</h3>
              <p>THIS PROCESS IS NOT REVERSABLE! PLEASE ENSURE YOU HAVE THE CORRECT ORDER</p>
              <strong class="big">{{ this.order.num }}</strong>
            </center>
          </b-form-row>
      </b-form>
      <div slot="modal-footer" class="w-100">
        <b-row class="justify-content-between">
          <b-col cols="*"><b-btn class="ml-2" variant="secondary" @click="toggleCancelModal">Keep Order</b-btn></b-col>
          <b-col cols="*"><b-btn class="mr-2" variant="danger" @click="cancelOrder">Cancel Order</b-btn></b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import orderMerge from '../../../services/orderMerge'
import BridgeService from '../../../services/bridge'
import ChampService from '../../../services/champ';
import Constants from '../../../../constants';

const BASE_API = process.env.VUE_API_ROOT

export default {
  props: {
    order: { type: Object, required: true },
  },
  data() {
    return {
      locked: false,
      lockedBy: '',
      fishbowlMessage: '',
      webMessage: '',
      waitingOnChamp: false,
      cantDeleteMessage: 'This order has a payment attached to it, and cannot be deleted.',
      quotesToImport: [],
      cancelReasons: [
        { value: null, text: "" },
        { value: 'CUSTOMER', text: "The customer wanted to cancel the order." },
        { value: 'DECLINED', text: "Payment was declined." },
        { value: 'FRAUD', text: "The order was fraudulent." },
        { value: 'INVENTORY', text: "There was insufficient inventory." },
        { value: 'OTHER', text: "The order was canceled for an unlisted reason." },
        { value: 'STAFF', text: "Staff made an error." },
      ],
      cancelAlert: undefined,
      cancelForm: {
        shouldNotify: true,
        cancelReason: null,
        refundType: 'original',
        adminNote: ''
      }
    };
  },
  mounted() {
    axios.get(`${BASE_API}/locks/${this.$route.params.id}`).then((r) => {
      this.locked = !!r.data;
      if (this.locked) {
        this.lockedBy = r.data.locked_by
      }
    }).catch(error => console.error(error))
    axios.get(`${BASE_API}/am-old-db/import-quotes`).then((r) => {
      this.quotesToImport = r.data
    }).catch(error => console.error(error))
  },
  computed: {
    ...mapGetters({
      canAccess: 'canAccess',
      loggedInUser: 'loggedInUser',
    }),
    inFishbowl() {
      return this.order.sources.includes('fishbowl') && this.order.status.fishbowl != 'doesnt_exist'
    },
    inProgress() {
      return orderMerge.mergeField(this.order.soStatus) >= 25
    },
    onWebsite() {
      return !!this.order.websiteName && this.order.num[0] !== 'R'
    },
    isQuote() {
      return orderMerge.mergeField(this.order.quote)
    },
    isCanceled() {
      return this.order.status[this.order.websiteName] == 'canceled'
    },
    hasFBPayments() {
      return this.order.payments.some(x => Object.keys(x.method).includes('fishbowl'))
    },
    imported() {
      if (this.onWebsite) {
        return orderMerge.mergeField(this.order.imported)
      }
      return true;
    },
    canLock() {
      return this.$store.getters.canAccess('it-staff')
    },
    canEdit() {
      return false
      return this.onWebsite || !this.isCanceled
    },
    canImport() {
      return this.onWebsite && !this.hasFBPayments && !this.isCanceled && (!this.inFishbowl || !this.inProgress)
    },
    canDelete() {
      return this.inFishbowl && !this.inProgress && !this.hasFBPayments && this.order.num[0] !== 'F'
    },
    canCancelImport() {
      return this.onWebsite && !this.imported && (!this.isQuote || this.quotesToImport.includes(this.order.num));
    },
    canImportQuote() {
      return false
      return this.isQuote && !this.inFishbowl && !this.quotesToImport.includes(this.order.num)
    },
    canRMAHC() {
      return this.$store.getters.canAccess('returns-info-access') &&
            this.inFishbowl &&
            Array.isArray(this.order.shippings) &&
            [20,30].some(x => this.order.shippings.filter(s => !!s.status).some(s => s.status.id.fishbowl == x))
    },
  },
  methods: {
    toggleLock() {
      let action = this.locked ? 'unlock' : 'lock'
      axios({
        method: 'post',
        headers: { 'Content-Type': 'text/plain' },
        url: `${BASE_API}/locks/${this.order.num}/${action}`,
        data: { 'who': this.loggedInUser.names.displayName }
      }).then(() => {
        this.infoAuditLog(action, '', this.order.num, null, null)
        this.reloadPage()
      })
    },
    showModal(message, message2 = '') {
      this.fishbowlMessage = message
      this.webMessage = message2
      this.$refs.statusModal.show()
    },
    deleteFb(event, callback) {
      if (!event) {
        event.preventDefault();
      }
      this.doUpdateAction((args) => {
        this.fishbowlMessage = "Working..."
        if (args.status && args.status.fishbowl === Constants.SO_STATUS_IN_PROGRESS) {
          this.fishbowlMessage = 'This order is In Progress - speak with Ops to delete it!';
          return;
        }
        if (args.status.fishbowl) {
          ChampService.deleteOrder(args.num).then((r) => {
            if (r.data.commandId) {
              this.checkChamp(r.data.commandId, event.target.innerText, callback);
            } else {
              console.warn('CHAMP data', r);
              this.fishbowlMessage = `We had a problem updating this order.
                Details have been logged to the console. Please summon an IT
                person to help investigate the problem.`;
            }
          })
        } else if (typeof callback === 'function') {
          callback();
        } else {
          this.fishbowlMessage = 'Done!'
        }
      }, { num: this.order.num, status: this.order.soStatus })
    },
    importQuote() {
      axios({
        headers: { 'Content-Type': 'text/plain' },
        url: `${BASE_API}/am-old-db/import-quotes`,
        method: 'post',
        params: { id: this.order.num },
      }).then(response => response === []).catch((error) => {
        console.error(error)
        return false
      })
    },
    cancelImportQuote() {
      axios({
        headers: { 'Content-Type': 'text/plain' },
        url: `${BASE_API}/am-old-db/cancel-import-quotes`,
        method: 'post',
        params: { id: this.order.num },
      }).then(response => response === []).catch((error) => {
        console.error(error)
        return false
      })
    },
    importOrder(issue = false) {
      this.doUpdateAction((args) => {
        this.fishbowlMessage = "Working..."
        if (args.status && args.status.fishbowl === Constants.SO_STATUS_IN_PROGRESS) {
          this.fishbowlMessage = 'This order is In Progress - speak with Ops to delete it!';
          return;
        }
        if (this.inFishbowl) {
          ChampService.deleteOrder(args.num).then((r) => {
            if (r.data.commandId) {
              this.checkChamp(r.data.commandId, event.target.innerText, null);
            } else {
              console.warn('CHAMP data', r);
              this.fishbowlMessage = `We had a problem updating this order.
                Details have been logged to the console. Please summon an IT
                person to help investigate the problem.`;
            }
          })
        }
        BridgeService.importOrder(args.num, args.issue).then(() => {
          this.webMessage = 'Order marked for import successfully! It should be imported within the next few minutes...'
        }).catch((error) => {
          console.error(error)
          this.webMessage = `There was an error!
            Details have been logged to the console. Please summon an IT
                person to help investigate the problem.`
        })
      }, { num: this.order.num, issue })
    },
    cancelImport() {
      this.doUpdateAction((args) => {
        BridgeService.cancelImport(args.num).then(() => {
          this.webMessage = 'Import cancelled.'
        }).catch((error) => {
          console.error(error)
          this.webMessage = `There was an error!
            Details have been logged to the console. Please summon an IT
                person to help investigate the problem.`
        })
      }, { num: this.order.num })
    },
    cancelOrder(event) {
      if (event) event.preventDefault();

      this.cancelAlert = undefined;
      if (this.cancelForm.cancelReason == null) {
        this.cancelAlert = "Please fill out Cancellation Reason!"
        return
      }

      if (this.inFishbowl) {
        this.fishbowlMessage = "Working..."
        ChampService.deleteOrder(this.order.num).then((r) => {
          if (r.data.commandId) {
            this.checkChamp(r.data.commandId, event.target.innerText, null);
          } else {
            console.warn('CHAMP data', r);
            this.fishbowlMessage = `We had a problem deleting this order from Fishbowl.
              Details have been logged to the console. Please summon an IT
              person to help investigate the problem.`;
          }
        })
      }

      this.doUpdateAction((args) => {
        BridgeService.cancelOrder(args.num, args.form).then(() => {
          this.webMessage = 'Order Cancelled.'
        }).catch((error) => {
          console.error(error)
          this.webMessage = `There was an error cancelling this order on Shopify!
            Details have been logged to the console. Please summon an IT
            person to help investigate the problem.`
        })
      }, {
        num: this.order.num,
        form: this.cancelForm
      })
    },
    doUpdateAction(action, args) {
      this.webMessage = 'Working...';
      this.toggleStatus();

      BridgeService.getSalesOrder(this.order.num).then((updateCheck) => {
        if (this.order.websiteName && updateCheck.data.web.updatedAt !== this.order.updatedAt[this.order.websiteName]) {
          this.webMessage = 'Something else has made changes to this order! Please reload and try again.';
          return
        }
        action(args)
      });
    },
    toggleCancelModal() {
      this.$refs['cancel-confirmation'].toggle()
    },
    toggleStatus() {
      this.$refs['status-modal'].toggle()
    },
    reloadPage() {
      window.location.reload()
    },
    checkChamp(id, buttonText, callback) {
      ChampService.getCommandStatus(id).then((r) => {
        if (r.data.Processed) {
          if (r.data.ErrorCode === 1000) {
            this.infoAuditLog('deleted', buttonText, this.soNum, null, null);
            if (typeof callback === 'function') {
              callback();
            } else {
              this.fishbowlMessage = 'Done!';
              this.waitingOnChamp = false;
            }
          } else {
            console.log('champ response data', r);
            this.waitingOnChamp = false;
            if (r.data.ErrorCode === 4100 && r.data.Response.includes('payment')) {
              this.fishbowlMessage = `This order can not be deleted from Fishbowl
                due to an attached payment, you must verify this order matches Workarea
                and issue it manually.  If canceling the order, please speak with IT`;
            } else if (r.data.ErrorCode === 4210) {
              this.fishbowlMessage = `This order can not be deleted from Fishbowl as a
                Manufacture Order has been started.  Please ask Operations if they can
                cancel the Manufacture Order to proceed, if they are unable, please see IT`;
            } else if (r.data.ErrorCode === 1011) {
              this.fishbowlMessage = `We were unable to delete the order from
                Fishbowl. This is because the order is not in Fishbowl
                <br>CHAMP Error: ${r.data.ErrorCode}/${r.data.Response}`
            } else {
              this.fishbowlMessage = `We were unable to delete the order from
                Fishbowl. This probably means Ops has started working on some part
                of the order. Check with them to make your changes.
                <br>CHAMP Error: ${r.data.ErrorCode}/${r.data.Response}`;
            }
          }
        } else {
          setTimeout(() => { this.checkChamp(id, buttonText, callback); }, 2000);
        }
      });
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/sales-order/view-order-2/Toolbar.vue